import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Download, FileSpreadsheet, Loader2, Zap } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const Export = () => {
  const [loading, setLoading] = useState(false);
  const [enelLoading, setEnelLoading] = useState(false);
  const [agencies, setAgencies] = useState([]);
  const [agents, setAgents] = useState([]);

  const [filters, setFilters] = useState({
    format: "xlsx",
    status_enel: "",
    agentie: "",
    date_from: "",
    date_to: ""
  });

  const [enel, setEnel] = useState({
    date_from: "",
    date_to: "",
    agency_id: "",
    agent_ref_id: "",
    supplier: "PPC",
  });

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [agRes, agentRes] = await Promise.all([
          axios.get(`${API_URL}/agencies`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/agents?limit=500&active=true`).catch(() => ({ data: { data: [] } })),
        ]);
        setAgencies(agRes.data || []);
        setAgents(agentRes.data?.data || []);
      } catch {
        // ignore lookups errors silently — UI degrades gracefully
      }
    };
    fetchLookups();
  }, []);

  const handleExport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("format", filters.format);
      if (filters.status_enel) params.set("status_enel", filters.status_enel);
      if (filters.agentie) params.set("agentie", filters.agentie);
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);

      const response = await axios.get(`${API_URL}/export?${params.toString()}`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `contracte_export.${filters.format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export finalizat!");
    } catch {
      toast.error("Endpoint generic /api/export nu este implementat încă. Folosește exportul ENEL.");
    } finally {
      setLoading(false);
    }
  };

  const handleEnelExport = async () => {
    setEnelLoading(true);
    try {
      const params = new URLSearchParams();
      if (enel.date_from) params.set("date_from", enel.date_from);
      if (enel.date_to) params.set("date_to", enel.date_to);
      if (enel.agency_id) params.set("agency_id", enel.agency_id);
      if (enel.agent_ref_id) params.set("agent_ref_id", enel.agent_ref_id);
      if (enel.supplier) params.set("supplier", enel.supplier);

      const response = await axios.get(`${API_URL}/exports/enel?${params.toString()}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
      );
      const link = document.createElement("a");
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      link.setAttribute("download", `enel_export_${stamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export ENEL finalizat!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la export ENEL");
    } finally {
      setEnelLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Export Date</h1>
        <p className="text-slate-500 mt-1">
          Rapoarte Excel — generic și raport ENEL/PPC pe modelul actual
        </p>
      </div>

      <Tabs defaultValue="enel" className="w-full">
        <TabsList>
          <TabsTrigger value="enel" data-testid="tab-enel">
            <Zap className="h-4 w-4 mr-2" />Export ENEL
          </TabsTrigger>
          <TabsTrigger value="generic" data-testid="tab-generic">
            <FileSpreadsheet className="h-4 w-4 mr-2" />Export generic
          </TabsTrigger>
        </TabsList>

        {/* ENEL TAB */}
        <TabsContent value="enel">
          <Card>
            <CardHeader>
              <CardTitle>Raport ENEL / PPC</CardTitle>
              <CardDescription>
                Filtre: perioadă (signed_date), agenție, agent (commercial — agent_ref_id), furnizor. RBAC aplicat: managerul vede doar agențiile lui, agentul doar propriile vânzări.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data semnării de la</Label>
                  <Input
                    type="date"
                    value={enel.date_from}
                    onChange={(e) => setEnel({ ...enel, date_from: e.target.value })}
                    data-testid="enel-date-from"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data semnării până la</Label>
                  <Input
                    type="date"
                    value={enel.date_to}
                    onChange={(e) => setEnel({ ...enel, date_to: e.target.value })}
                    data-testid="enel-date-to"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Agenție</Label>
                  <Select
                    value={enel.agency_id || "all"}
                    onValueChange={(v) => setEnel({ ...enel, agency_id: v === "all" ? "" : v })}
                  >
                    <SelectTrigger data-testid="enel-agency"><SelectValue placeholder="Toate" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toate agențiile</SelectItem>
                      {agencies.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Agent</Label>
                  <Select
                    value={enel.agent_ref_id || "all"}
                    onValueChange={(v) => setEnel({ ...enel, agent_ref_id: v === "all" ? "" : v })}
                  >
                    <SelectTrigger data-testid="enel-agent"><SelectValue placeholder="Toți" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toți agenții</SelectItem>
                      {agents.map(a => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.full_name || a.id}{a.primary_marca_code ? ` (${a.primary_marca_code})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Furnizor</Label>
                <Select value={enel.supplier} onValueChange={(v) => setEnel({ ...enel, supplier: v })}>
                  <SelectTrigger data-testid="enel-supplier"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PPC">PPC (implicit)</SelectItem>
                    <SelectItem value="Enel">Enel (legacy)</SelectItem>
                    <SelectItem value="all">Toți furnizorii</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={handleEnelExport}
                disabled={enelLoading}
                className="w-full bg-amber-600 hover:bg-amber-700"
                data-testid="enel-export-btn"
              >
                {enelLoading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Se generează raportul...</>
                ) : (
                  <><Download className="h-4 w-4 mr-2" />Descarcă Raport ENEL (.xlsx)</>
                )}
              </Button>

              <Card className="bg-amber-50/50 border-amber-200">
                <CardContent className="pt-5 text-sm text-amber-900 space-y-1">
                  <p><strong>Coloane incluse:</strong> Cod Marca, Agent, Agenție, POD, NLC, CLC, Tip Client, Nume/Firmă, CNP/CUI, Județ, Localitate, Telefon, Email, Furnizor, Produs, Distribuitor, Zonă, kWh, Data semnării, Data activare, Status, Canal, ID extern.</p>
                  <p><strong>Sheet Meta:</strong> include perioada, autorul, totalul de rânduri.</p>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </TabsContent>

        {/* GENERIC TAB */}
        <TabsContent value="generic">
          <Card>
            <CardHeader>
              <CardTitle>Export generic contracte</CardTitle>
              <CardDescription>
                Configurare format + filtre simple (legacy)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Format Export</Label>
                <Select
                  value={filters.format}
                  onValueChange={(value) => setFilters({ ...filters, format: value })}
                >
                  <SelectTrigger data-testid="format-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                    <SelectItem value="csv">CSV (.csv)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-medium text-slate-900">Filtre (opțional)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select
                      value={filters.status_enel || "all"}
                      onValueChange={(value) => setFilters({ ...filters, status_enel: value === "all" ? "" : value })}
                    >
                      <SelectTrigger data-testid="status-filter">
                        <SelectValue placeholder="Toate statusurile" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Toate</SelectItem>
                        <SelectItem value="Draft">Draft</SelectItem>
                        <SelectItem value="OK">OK</SelectItem>
                        <SelectItem value="KO">KO</SelectItem>
                        <SelectItem value="Trimis">Trimis</SelectItem>
                        <SelectItem value="Necesită Corecție">Necesită Corecție</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Agenție (text)</Label>
                    <Input
                      placeholder="Filtrează după agenție"
                      value={filters.agentie}
                      onChange={(e) => setFilters({ ...filters, agentie: e.target.value })}
                      data-testid="agentie-filter"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Data semnării de la</Label>
                    <Input
                      type="text"
                      placeholder="DD.MM.YYYY"
                      value={filters.date_from}
                      onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                      data-testid="date-from"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Data semnării până la</Label>
                    <Input
                      type="text"
                      placeholder="DD.MM.YYYY"
                      value={filters.date_to}
                      onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                      data-testid="date-to"
                    />
                  </div>
                </div>
              </div>

              <Button
                onClick={handleExport}
                disabled={loading}
                className="w-full"
                data-testid="export-btn"
              >
                {loading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Se exportă...</>
                ) : (
                  <><Download className="h-4 w-4 mr-2" />Exportă Contracte</>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Export;
